import { type FC, useState, useCallback, useMemo } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Link } from "react-router-dom";
import { formatCreditsExact } from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";
import { EditTeamBudgetDialog } from "@/routes/settings/organization/enterprise-usage/EditTeamBudgetDialog";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { SortableTableHead, type TableSortOrder } from "@/components/flexkit/tables/SortableTable";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { TeamCumulativeCreditUsage } from "gitpod-next-api/gitpod/v1/billing_pb";

type PlainTeamCumulativeCreditUsage = PlainMessage<TeamCumulativeCreditUsage>;

type Props = {
    teams: PlainTeamCumulativeCreditUsage[];
    organizationId: string;
    /** e.g. "Since Jan 1, 2025" or "Since Jan 1, 2025 · Up to Feb 28, 2026" */
    periodLabel?: string;
    /** Whether the currently selected billing period is the current month */
    isCurrentMonth: boolean;
};

const DELETED_TEAM_DISPLAY_NAME = "(deleted team)";

function isDeletedTeam(team: PlainTeamCumulativeCreditUsage): boolean {
    return team.displayName === DELETED_TEAM_DISPLAY_NAME;
}

type SortColumn = "team" | "usage" | "budget" | "budgetUsed";

function getTeamCredits(team: PlainTeamCumulativeCreditUsage): number {
    return team.usage?.totalCredits ?? 0;
}

function getTeamBudget(team: PlainTeamCumulativeCreditUsage): number | undefined {
    return team.creditBudget !== undefined ? Number(team.creditBudget) : undefined;
}

function getTeamBudgetPct(team: PlainTeamCumulativeCreditUsage): number | undefined {
    const budget = getTeamBudget(team);
    return budget && budget > 0 ? Math.round((getTeamCredits(team) / budget) * 100) : undefined;
}

function compareTeams(
    a: PlainTeamCumulativeCreditUsage,
    b: PlainTeamCumulativeCreditUsage,
    column: SortColumn,
    order: TableSortOrder,
): number {
    // Deleted teams always sort to the bottom
    const aDeleted = isDeletedTeam(a);
    const bDeleted = isDeletedTeam(b);
    if (aDeleted !== bDeleted) return aDeleted ? 1 : -1;

    const dir = order === "asc" ? 1 : -1;

    switch (column) {
        case "team":
            return dir * a.displayName.localeCompare(b.displayName);
        case "usage":
            return dir * (getTeamCredits(a) - getTeamCredits(b));
        case "budget": {
            const aBudget = getTeamBudget(a);
            const bBudget = getTeamBudget(b);
            // Teams without a budget sort after teams with one
            if (aBudget === undefined && bBudget === undefined) return 0;
            if (aBudget === undefined) return 1;
            if (bBudget === undefined) return -1;
            return dir * (aBudget - bBudget);
        }
        case "budgetUsed": {
            const aPct = getTeamBudgetPct(a);
            const bPct = getTeamBudgetPct(b);
            // Teams without a percentage sort after teams with one
            if (aPct === undefined && bPct === undefined) return 0;
            if (aPct === undefined) return 1;
            if (bPct === undefined) return -1;
            return dir * (aPct - bPct);
        }
    }
}

export const TeamUsageTable: FC<Props> = ({ teams, organizationId, periodLabel, isCurrentMonth }) => {
    const [editingTeam, setEditingTeam] = useState<PlainTeamCumulativeCreditUsage | null>(null);
    const [sortColumn, setSortColumn] = useState<SortColumn>("team");
    const [sortOrder, setSortOrder] = useState<TableSortOrder>("asc");

    const handleSort = useCallback((column: unknown, order: TableSortOrder) => {
        if (column === null) {
            // Reset to default sort: alphabetical by team name
            setSortColumn("team");
            setSortOrder("asc");
            return;
        }
        setSortColumn(column as SortColumn);
        setSortOrder(order);
    }, []);

    const sortedTeams = useMemo(
        () => [...teams].sort((a, b) => compareTeams(a, b, sortColumn, sortOrder)),
        [teams, sortColumn, sortOrder],
    );

    const handleEditClose = useCallback(() => setEditingTeam(null), []);

    if (sortedTeams.length === 0) {
        return (
            <div data-testid="team-usage-table">
                <Text className="text-sm text-content-tertiary">
                    {isCurrentMonth ? (
                        <>
                            No teams found. Create teams in{" "}
                            <Link to="/settings/members" className="underline hover:text-content-secondary">
                                Settings → Members
                            </Link>{" "}
                            to track team-level usage.
                        </>
                    ) : (
                        "No teams existed during this billing period."
                    )}
                </Text>
            </div>
        );
    }

    return (
        <div data-testid="team-usage-table">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <Text className="text-lg font-semibold text-content-primary">Team credit usage</Text>
                    {periodLabel && <Text className="mt-0.5 text-xs text-content-tertiary">{periodLabel}</Text>}
                </div>
            </div>

            <Table className="text-sm">
                <TableHeader className="border-b border-border-light [&_th]:bg-surface-secondary [&_th]:font-medium [&_th]:text-content-secondary">
                    <TableRow>
                        <SortableTableHead
                            columnName={"team" as SortColumn}
                            sortOrder={sortColumn === "team" ? sortOrder : null}
                            initialSortOrder="asc"
                            onSort={handleSort}
                            className="text-left"
                        >
                            Team
                        </SortableTableHead>
                        <SortableTableHead
                            columnName={"usage" as SortColumn}
                            sortOrder={sortColumn === "usage" ? sortOrder : null}
                            initialSortOrder="desc"
                            onSort={handleSort}
                            className="text-right"
                        >
                            Cumulative total
                        </SortableTableHead>
                        <SortableTableHead
                            columnName={"budget" as SortColumn}
                            sortOrder={sortColumn === "budget" ? sortOrder : null}
                            initialSortOrder="desc"
                            onSort={handleSort}
                            className="text-right"
                        >
                            Budget
                        </SortableTableHead>
                        <SortableTableHead
                            columnName={"budgetUsed" as SortColumn}
                            sortOrder={sortColumn === "budgetUsed" ? sortOrder : null}
                            initialSortOrder="desc"
                            onSort={handleSort}
                            className="text-right"
                        >
                            Budget used
                        </SortableTableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sortedTeams.map((team) => {
                        const totalCredits = getTeamCredits(team);
                        const budget = getTeamBudget(team);
                        const budgetPct = getTeamBudgetPct(team);
                        const isOverBudget = budgetPct !== undefined && budgetPct > 100;

                        return (
                            <TableRow
                                key={team.teamId}
                                className="border-b border-border-light last:border-b-0 hover:bg-surface-hover"
                            >
                                <TableCell className="py-3">
                                    {isDeletedTeam(team) ? (
                                        <span className="italic text-content-tertiary">
                                            {team.displayName}
                                        </span>
                                    ) : (
                                        <Link
                                            to={`/settings/members/team/${team.teamId}`}
                                            className="font-medium text-content-link hover:underline"
                                            data-tracking-id="team-usage-manage-link"
                                        >
                                            {team.displayName}
                                        </Link>
                                    )}
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    <span className="font-medium text-content-primary">
                                        {formatCreditsExact(totalCredits)}
                                    </span>
                                    <span className="ml-1 text-content-tertiary">credits</span>
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    {isDeletedTeam(team) ? (
                                        <span className="text-content-tertiary">&mdash;</span>
                                    ) : budget !== undefined ? (
                                        <button
                                            type="button"
                                            className="cursor-pointer text-content-primary hover:text-content-secondary"
                                            onClick={() => setEditingTeam(team)}
                                            data-tracking-id="team-usage-edit-budget"
                                        >
                                            <span className="font-medium underline decoration-dotted">
                                                {formatCreditsExact(budget)}
                                            </span>
                                            <span className="ml-1 text-content-tertiary">credits</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="cursor-pointer text-content-tertiary underline decoration-dotted hover:text-content-secondary hover:no-underline"
                                            onClick={() => setEditingTeam(team)}
                                            data-tracking-id="team-usage-set-budget"
                                        >
                                            Set budget
                                        </button>
                                    )}
                                </TableCell>
                                <TableCell className="py-3 text-right">
                                    {budgetPct !== undefined ? (
                                        <span
                                            className={
                                                isOverBudget
                                                    ? "font-semibold text-content-destructive"
                                                    : "text-content-primary"
                                            }
                                        >
                                            {budgetPct}%
                                        </span>
                                    ) : (
                                        <span className="text-content-tertiary">&mdash;</span>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Text className="mt-2 text-xs text-content-tertiary">
                A member&apos;s credit usage counts toward a team only for the period they belong to it. Adding or
                removing members changes future attribution but does not affect historical data. Team budgets apply
                since the team was created. You can adjust a team&apos;s budget at any time to reflect changing needs.
            </Text>

            {editingTeam && (
                <EditTeamBudgetDialog
                    open={!!editingTeam}
                    onClose={handleEditClose}
                    organizationId={organizationId}
                    teamId={editingTeam.teamId}
                    teamName={editingTeam.displayName}
                    currentBudget={
                        // optional int64 — undefined means no allocation configured
                        editingTeam.creditBudget !== undefined ? Number(editingTeam.creditBudget) : undefined
                    }
                />
            )}
        </div>
    );
};
